/**
 * The named option sets behind formatNumber, formatCurrency and formatPercent (@/lib/formatters,
 * which re-exports them). Kept apart with no imports so the extension's stand-in for the formatters
 * (extension/lib/formatters.ts) shares them. Rules: formatting.mdc, formatting.project.mdc.
 */

/** Named option sets for formatNumber. */
export const NUMBER_PRESETS = {
  /** 1,284 */
  integer: { maximumFractionDigits: 0 },
  /** Up to 2 places: 12.5, 3 */
  decimal: { maximumFractionDigits: 2 },
  /** Always 1 place: 3.0, 12.5 */
  oneDecimal: { minimumFractionDigits: 1, maximumFractionDigits: 1 },
  /** 12.9K */
  compact: { notation: 'compact', maximumFractionDigits: 1 },
  /** A signed count change: +12, −3, 0 */
  change: { maximumFractionDigits: 0, signDisplay: 'exceptZero' },
} satisfies Record<string, Intl.NumberFormatOptions>;
export type NumberPreset = keyof typeof NUMBER_PRESETS;

/** Named option sets for formatCurrency ('price' shows cents only when the amount has them). */
export const CURRENCY_PRESETS = {
  /** R 1,299.50 (the default) */
  standard: {},
  /** R 1,300 */
  whole: { minimumFractionDigits: 0, maximumFractionDigits: 0 },
  /** R 12.9K */
  compact: { notation: 'compact', maximumFractionDigits: 1 },
  /** Unit prices kept to 4 places: R 12.3456 */
  unitPrice: { minimumFractionDigits: 2, maximumFractionDigits: 4 },
  /** A signed change: +R 120.00 */
  change: { signDisplay: 'exceptZero' },
  /** R 899, R 899.50 */
  price: {},
} satisfies Record<string, Intl.NumberFormatOptions>;
export type CurrencyPreset = keyof typeof CURRENCY_PRESETS;

/** Percent decimals by what's measured (decision 59). formatPercent takes a fraction: 0.123 → 12.3%. */
export const PERCENT_PRESETS = {
  /** Margin, sellability, conversion: 12.3% */
  ratio: { minimumFractionDigits: 1, maximumFractionDigits: 1 },
  /** Click-through rate: 1.23% */
  ctr: { minimumFractionDigits: 2, maximumFractionDigits: 2 },
  /** A change: +12%, −3%, 0% */
  change: { maximumFractionDigits: 0, signDisplay: 'exceptZero' },
  /** A score: 87% */
  score: { maximumFractionDigits: 0 },
} satisfies Record<string, Intl.NumberFormatOptions>;
export type PercentPreset = keyof typeof PERCENT_PRESETS;
